import React, { FC, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Box, Typography } from "@mui/material";
import qs from "query-string";
import {
  IAnalyticsSummary,
  IChartRange,
  chartDataFromSummary,
  dayParam,
  initialChartState,
  viewerTimeZone,
} from "./helper";
import { RangeControl } from "../videos/selectedVideoCardV2/performance/RangeControl";
import { ProjectSelect } from "./projectSelect";
import { AnalyticsGraph } from "./analyticsGraph";
import { AnalyticsSummary } from "./analyticsSummary";
import axiosInstant from "../../../api";
import { getVideoFileAnalytics } from "../../../api/endpoints";
import { ReactComponent as LoadingIcon } from "../../../assets/svg/loading.svg";
import { SourceSelect } from "./srouceSelect";

/**
 * The V1 Analytics tab (/videos/ugc, /videos/xgc, /my-videos, TDC V1).
 *
 * One request (TDC-1676): the API returns the totals and a per-day series for
 * the window, counted in SQL and bucketed in the viewer's time zone. It used to
 * make three — every play, every per-second duration row, every embed
 * pageview — and count them here. With no window asked for the API answers
 * with the video's whole history; a pick in the range picker asks again.
 */

interface Props {
  brandId: any;
  videoId: number;
}

const rangeKeyOf = (range?: IChartRange) =>
  range ? `${dayParam(range[0])}|${dayParam(range[1])}` : "";

export const VideoAnalytics: FC<Props> = ({ brandId, videoId }) => {
  const [summary, setSummary] = useState<IAnalyticsSummary>();
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [chartData, setChartData] = useState(initialChartState);
  const [requestedRange, setRequestedRange] = useState<IChartRange>();
  const [projectId, setProjectId] = useState<string>();
  const [source, setSource] = useState<string>();

  const requestedKey = rangeKeyOf(requestedRange);

  useEffect(() => {
    if (videoId && brandId) {
      getAnalytics();
    }
  }, [videoId, brandId, projectId, source, requestedKey]);

  const getAnalytics = async () => {
    const payload = qs.stringify({
      project: projectId,
      source,
      tz: viewerTimeZone(),
      from: requestedRange ? dayParam(requestedRange[0]) : undefined,
      to: requestedRange ? dayParam(requestedRange[1]) : undefined,
    });
    try {
      const { data } = await axiosInstant.get<IAnalyticsSummary>(
        getVideoFileAnalytics(brandId, videoId, payload),
      );
      setSummary(data);
      setChartData(chartDataFromSummary(data));
      setFailed(false);
    } catch (err) {
      setSummary(undefined);
      setFailed(true);
    }
    setLoading(false);
  };

  // The window on screen: the user's pick, else the one the payload was
  // counted over (the video's whole history when nothing was asked for).
  const shownRange = useMemo<IChartRange | undefined>(() => {
    if (requestedRange) return requestedRange;
    if (summary) return [dayjs(summary.from).startOf("day"), dayjs(summary.to).startOf("day")];
    return undefined;
  }, [requestedRange, summary]);
  const earliest = summary?.earliest_day ? dayjs(summary.earliest_day).startOf("day") : null;

  const showMetrics = !!summary && summary.earliest_day !== null;
  const analyticsExists = !!summary && summary.daily.some((d) => d.watch_seconds > 0 || d.plays > 0);
  return (
    <Box className="my-6">
      <Box
        className="border-b border-border-token"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        pb={2}
        color="#fff"
      >
        <div className="text-2xl font-CircularStdBlack text-text-1 font-extrabold w-full">
          Analytics
        </div>

        <Box
          display="flex"
          width="100%"
          justifyContent="flex-end"
          alignItems="center"
          gap={2}
          color="#fff"
        >
          {/* The license-free range control the V2 Performance panel uses. The
              MUI X Pro DateRangePicker that was here throws on every
              non-production build since the Pro key expired (2026-04-08),
              which crashed this tab on local and staging. */}
          {showMetrics && shownRange && (
            <RangeControl range={shownRange} earliest={earliest} onChange={setRequestedRange} />
          )}
          <SourceSelect {...{ source, setSource }} />
          <ProjectSelect
            brandId={brandId}
            projectId={projectId}
            setProjectId={setProjectId}
          />
        </Box>
      </Box>
      {loading ? (
        <LoadingIcon width="70px" />
      ) : (
        <>
          {summary && (
            <Box mb={2}>
              {showMetrics && <AnalyticsSummary totals={summary.totals} />}
              {analyticsExists && (
                <>
                  <div className="text-2xl font-CircularStdBlack font-extrabold w-full mb-6">
                    Video Analytics Graph
                  </div>
                  <AnalyticsGraph data={chartData} />
                </>
              )}
            </Box>
          )}
          {failed && (
            <Typography variant="subtitle1" textAlign="center">
              Analytics could not be loaded. Please try again.
            </Typography>
          )}
          {!failed && !showMetrics && (
            <Typography variant="subtitle1" textAlign="center">
              There is no analytic data for this video.
            </Typography>
          )}
          {showMetrics && !analyticsExists && (
            <Typography variant="subtitle1" mt={5} textAlign="center">
              There is no analytic data to show in graph.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};
